export const formatDate = (dateString: string | null | undefined, isDueDate = true) => {
  if (!dateString) {
    return isDueDate ? "No Due Date" : "No Date";
  }
  // Parse the date string into a Date object
  const date = new Date(dateString);
  // Create an array of month names to convert month number to month name
  const monthNames = [
    "January,",
    "February,",
    "March,",
    "April,",
    "May,",
    "June,",
    "July,",
    "August,",
    "September,",
    "October,",
    "November,",
    "December,",
  ];
  // Construct the formatted date string
  const formattedDate = `${isDueDate ? "" : ""} ${monthNames[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;

  return formattedDate;
};

export function formatDateAsDayOrFutureDate(date: Date) {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Remove the time portion of the dates for accurate comparison
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tomorrowStart = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateStart.getTime() === todayStart.getTime()) {
    return "today";
  } else if (dateStart.getTime() === tomorrowStart.getTime()) {
    return "tomorrow";
  } else {
    const differenceInDays = (dateStart - todayStart) / (1000 * 60 * 60 * 24);
    if (differenceInDays >= 0 && differenceInDays <= 7) {
      return daysOfWeek[dateStart.getDay()];
    }
  }

  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

export const formatSubmittedDate = (dateString: string | null | undefined) => {
  if (!dateString) return "";

  try {
    // Parse the date string (assuming format like "2025-08-07T16:30:01.000Z")
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Format date as "Feb 22, 2025"
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    // Return the date
    return formattedDate;
  } catch (error) {
    // Return original string if parsing fails
    return dateString;
  }
};

export const formatSubmittedTime = (dateString: string | null | undefined) => {
  if (!dateString) return "";

  try {
    // Parse the date string (assuming format like "2025-08-07T16:30:01.000Z")
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Format time as "11:47 PM"
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    // Return the time
    return formattedTime;
  } catch (error) {
    // Return original string if parsing fails
    return dateString;
  }
};

export const formatDateWithTime = (dateString: string | null | undefined) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  // Format: Mar 15, 2024 at 2:30 PM
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();

  // Format time as H:MM AM/PM (no leading zero for hours)
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${month} ${day}, ${year} at ${displayHours}:${displayMinutes} ${ampm}`;
};

export const formatSemester = (dateString: string | null | undefined) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const month = date.getMonth() + 1; // 1-12
  const year = date.getFullYear();

  // Fall: Aug-Dec (8-12)
  // Spring: Jan-May (1-5)
  // Summer: Jun-Jul (6-7)
  if (month >= 8 || month <= 0) {
    return `Fall ${year}`;
  } else if (month >= 1 && month <= 5) {
    return `Spring ${year}`;
  } else {
    return `Summer ${year}`;
  }
};

export const formatDateForDisplay = (dateString: string | null | undefined) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

type Neo4jDatePart = {
  low: number;
};

type Neo4jDateObject = {
  year: Neo4jDatePart;
  month: Neo4jDatePart;
  day: Neo4jDatePart;
  hour?: Neo4jDatePart;
  minute?: Neo4jDatePart;
  second?: Neo4jDatePart;
};

type DateInput = Date | string | number | Neo4jDateObject | null | undefined;

export function parseDate(dateObj: DateInput) {
  if (dateObj === null || dateObj === undefined) {
    return new Date(NaN);
  }

  if (dateObj && typeof dateObj === "object" && "year" in dateObj && "month" in dateObj && "day" in dateObj) {
    // Ignore milliseconds by not including them in the new Date() constructor
    return new Date(
      dateObj.year.low,
      dateObj.month.low - 1,
      dateObj.day.low,
      dateObj.hour ? dateObj.hour.low : 0,
      dateObj.minute ? dateObj.minute.low : 0,
      dateObj.second ? dateObj.second.low : 0,
      0 // Set milliseconds to 0
    );
  }
  let date = new Date(dateObj);
  date.setMilliseconds(0);
  return date;
}

export const formatTimeAgo = (dateString: string | null | undefined) => {
  if (!dateString) return "N/A";

  const now = new Date();
  const eventDate = new Date(dateString);

  if (isNaN(eventDate.getTime())) return "N/A";

  const diffInSeconds = Math.floor((now.getTime() - eventDate.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    if (days === 1) {
      return "yesterday";
    } else {
      return `${days} days ago`;
    }
  }
};

/**
 * Checks if a submission is late based on the submission date and assignment deadlines
 * @param {Object} params - Parameters object
 * @param {string|null|undefined} params.submittedDate - The submission date (completedAt or created)
 * @param {string|null|undefined} params.dueDate - The assignment due_date
 * @param {string|null|undefined} params.retakeDeadline - The retake deadline (for resubmissions)
 * @returns {boolean} - True if the submission is late, false otherwise
 */
type SubmissionLateParams = {
  submittedDate: string | null | undefined;
  dueDate: string | null | undefined;
  retakeDeadline: string | null | undefined;
};

export const isSubmissionLate = ({ submittedDate, dueDate, retakeDeadline }: SubmissionLateParams) => {
  if (!submittedDate) {
    return false;
  }

  const deadlineValue = retakeDeadline || dueDate;
  if (!deadlineValue) {
    return false;
  }

  const submissionDate = new Date(submittedDate);
  const deadlineDate = new Date(deadlineValue);
  if (isNaN(deadlineDate.getTime()) || isNaN(submissionDate.getTime())) {
    return false;
  }

  deadlineDate.setHours(0, 0, 0, 0);
  submissionDate.setHours(0, 0, 0, 0);
  return submissionDate > deadlineDate;
};
